import React, { useRef, useState } from 'react'
import { useTranslation } from '~/hooks/useTranslation'
import { Modal, Stack } from '~/components/_layouts'
import { useBenefits } from '../BenefitsProvider'
import {
  MessagingProvider,
  useMessaging,
} from '~/components/CoachMessaging/MessagingProvider'
import { BrowserRouter } from 'react-router'
import { Button } from '~/lib/ui/button'
import { apiPost } from '~/util/api'
import { useMutation } from '@tanstack/react-query'
import { Input } from '~/lib/ui/input'
import RichTextEditor from '~/lib/ui/rich-text-editor'
import { SpinnerWhite } from '~/components/shared/Spinner'
import { useToast } from '~/lib/ui/toast'

const MessageCoachDialogInner = ({ subject, isOpen, onClose }) => {
  const { t } = useTranslation('CoachMessageThread')
  const { queryClient } = useMessaging()
  const defaultSubject = t('resume_review_subject')
  const [newMessageSubject, setNewMessageSubject] = useState(
    subject || defaultSubject,
  )
  const [loading, setLoading] = useState(false)
  const [sentMode, setSentMode] = useState(false)
  const editorRef = useRef(null)
  const { toast } = useToast()

  const resetDialog = () => {
    onClose()
    setTimeout(() => {
      setSentMode(false)
      setNewMessageSubject(defaultSubject)
      editorRef.current.setContents([])
    }, 100)
  }

  const postNewThread = async (request) => {
    const response = await apiPost(`/coach_messages.json`, request)
    return response
  }

  const createThreadMutation = useMutation({
    mutationFn: postNewThread,
    onSuccess: (res) => {
      setSentMode(true)
      setLoading(false)
      queryClient.invalidateQueries(['messageThreads'])
      setNewMessageSubject(defaultSubject)
      editorRef.current.setContents([])
      return !!res
    },
    onError: (error) => {
      console.error('Error creating message:', error)
      setLoading(false)
      toast({
        description: t('an_error_occurred'),
        variant: 'destructive',
      })
    },
  })

  const createThread = () => {
    setLoading(true)
    const newMessageBody = editorRef.current.getSemanticHTML()

    const request = {
      coach_message_thread: {
        subject: newMessageSubject,
        body: newMessageBody,
      },
    }
    createThreadMutation.mutate(request)
  }

  return (
    <Modal
      title={
        <Stack direction="row" spacing={2}>
          {t('message_your_coach')}
        </Stack>
      }
      size="md"
      open={isOpen}
      onClose={onClose}
      variant="secondary"
      actions={
        sentMode ? (
          <div className="flex flex-row justify-start gap-x-2">
            <Button onClick={resetDialog} variant="outline">
              {t('close')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-row justify-start gap-x-2">
            <Button onClick={createThread} variant="coachcta">
              {loading ? (
                <SpinnerWhite show={true} className="ml-4 size-5" />
              ) : (
                t('send')
              )}
            </Button>
            <Button onClick={onClose} variant="outline">
              {t('cancel')}
            </Button>
          </div>
        )
      }
    >
      {sentMode ? (
        <SentMode />
      ) : (
        <NewMessage
          newMessageSubject={newMessageSubject}
          setNewMessageSubject={setNewMessageSubject}
          editorRef={editorRef}
        />
      )}
    </Modal>
  )
}

const MessageCoachDialog = ({ ...props }) => {
  const { current_user } = useBenefits()
  const current_user_id = current_user?.id
  return (
    <BrowserRouter>
      <MessagingProvider
        current_user_id={current_user_id}
        program_tier="gold"
        resume_review={true}
      >
        <MessageCoachDialogInner {...props} />
      </MessagingProvider>
    </BrowserRouter>
  )
}
export default MessageCoachDialog

const NewMessage = ({ newMessageSubject, setNewMessageSubject, editorRef }) => {
  const { t } = useTranslation('CoachMessageThread')

  return (
    <div className="flex flex-col gap-y-4">
      <h2>
        {t('message')} {t('your_coach')}
      </h2>
      <div>
        <label>{t('subject')}</label>
        <Input
          value={newMessageSubject}
          onChange={(e) => setNewMessageSubject(e.target.value)}
          placeholder={t('enter_subject')}
        />
      </div>
      <div>
        <label>{t('message')}</label>
        <RichTextEditor ref={editorRef} />
      </div>
    </div>
  )
}

const SentMode = () => {
  const { t } = useTranslation('CoachMessageThread')
  return (
    <div className="flex flex-col gap-y-4">
      <div>{t('message_sent')}</div>
      <a target="_blank" rel="noopener noreferrer" href="/coach_messages">
        {t('view_message')}
      </a>
    </div>
  )
}
